import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';
import {
  getDocumentosEstudiante,
  subirDocumentoEstudiante,
  subirFotoEstudiante,
  eliminarDocumentoEstudiante,
  getUrlDescargaDocEstudiante,
} from '../../../api/students';

const TIPOS_DOCUMENTO = [
  { key: 'ACTA_NACIMIENTO', label: 'Acta de Nacimiento' },
  { key: 'CURP', label: 'CURP' },
  { key: 'COMPROBANTE_DOMICILIO', label: 'Comprobante de Domicilio' },
  { key: 'INE', label: 'INE (Mayores de Edad)' },
  { key: 'CONSTANCIA_ESTUDIOS', label: 'Constancia de Estudios' },
  { key: 'FOTO_PERFIL', label: 'Fotografía del Estudiante', isImage: true },
];

const FOTO_ALLOWED_EXT = ['jpg', 'jpeg', 'png'];
const FOTO_ALLOWED_MIME = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png'];
const FOTO_MAX_BYTES = 5 * 1024 * 1024;

function Documentos({ student, onFotoChanged }) {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [toast, setToast] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewIsImage, setPreviewIsImage] = useState(false);
  const fileInputRefs = useRef({});

  const estudianteId = student?.id_estudiante || student?.id;

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const cargarDocumentos = useCallback(async () => {
    if (!estudianteId) return;
    setLoading(true);
    try {
      const data = await getDocumentosEstudiante(estudianteId);
      const list = Array.isArray(data) ? data : (data?.results || []);
      setDocumentos(list);
    } catch (err) {
      console.error('Error cargando documentos del estudiante:', err);
    } finally {
      setLoading(false);
    }
  }, [estudianteId]);

  useEffect(() => {
    cargarDocumentos();
  }, [cargarDocumentos]);

  // Mapear documentos cargados por tipo
  const docPorTipo = {};
  documentos.forEach(doc => {
    docPorTipo[doc.tipo_documento] = doc;
  });

  const handleUploadClick = (tipoKey) => {
    const input = fileInputRefs.current[tipoKey];
    if (input) input.click();
  };

  const handleFileChange = async (tipoKey, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const tipoCfg = TIPOS_DOCUMENTO.find(t => t.key === tipoKey);
    const isImage = !!(tipoCfg && tipoCfg.isImage);
    const ext = (file.name.split('.').pop() || '').toLowerCase();

    if (isImage) {
      if (!FOTO_ALLOWED_EXT.includes(ext) || (file.type && !FOTO_ALLOWED_MIME.includes(file.type))) {
        showToast('Solo se permiten imágenes JPG, JPEG o PNG', 'error');
        event.target.value = '';
        return;
      }
      if (file.size > FOTO_MAX_BYTES) {
        showToast('La fotografía no debe superar 5 MB', 'error');
        event.target.value = '';
        return;
      }
    } else {
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        showToast('Solo se permiten archivos PDF', 'error');
        event.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showToast('El archivo no debe superar 10 MB', 'error');
        event.target.value = '';
        return;
      }
    }

    setUploading(tipoKey);
    try {
      if (isImage) {
        await subirFotoEstudiante(estudianteId, file);
        showToast('Fotografía cargada exitosamente');
        if (typeof onFotoChanged === 'function') onFotoChanged();
      } else {
        await subirDocumentoEstudiante(estudianteId, tipoKey, file);
        showToast('Documento cargado exitosamente');
      }
      await cargarDocumentos();
    } catch (err) {
      showToast('Error al subir: ' + (err?.response?.data?.error || err.message), 'error');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleViewDocument = (doc) => {
    const fmt = (doc.formato_archivo || '').toLowerCase();
    const isImg = doc.tipo_documento === 'FOTO_PERFIL' || ['jpg','jpeg','png'].includes(fmt);
    setPreviewIsImage(isImg);
    if (doc.archivo_url) {
      setPreviewUrl(doc.archivo_url);
    } else if (doc.ruta_archivo) {
      const base = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
      const mediaBase = base.replace('/api', '');
      setPreviewUrl(`${mediaBase}/media/${doc.ruta_archivo}`);
    }
  };

  const handleDownloadDocument = (doc) => {
    const token = localStorage.getItem('authToken');
    const url = getUrlDescargaDocEstudiante(doc.id_documento);
    fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(res => {
        if (!res.ok) throw new Error('Error al descargar');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc.nombre_archivo || 'documento.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => showToast('Error al descargar: ' + err.message, 'error'));
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`¿Eliminar ${doc.nombre_archivo}?`)) return;
    try {
      await eliminarDocumentoEstudiante(doc.id_documento);
      showToast('Documento eliminado');
      if (doc.tipo_documento === 'FOTO_PERFIL' && typeof onFotoChanged === 'function') {
        onFotoChanged();
      }
      await cargarDocumentos();
    } catch (err) {
      showToast('Error al eliminar: ' + err.message, 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalDocs = TIPOS_DOCUMENTO.length;
  const docsRecibidos = TIPOS_DOCUMENTO.filter(t => docPorTipo[t.key]).length;

  return (
    <div className={styles.tabPanel}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 99999,
          padding: '12px 24px', borderRadius: '8px',
          background: toast.type === 'error' ? '#f44336' : '#4caf50',
          color: '#fff', fontWeight: 600, fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', zIndex: 99998,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => { setPreviewUrl(null); setPreviewIsImage(false); }}
        >
          <div style={{ width: '80%', height: '85%', background: '#fff', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 600 }}>Vista previa del documento</span>
              <button onClick={() => { setPreviewUrl(null); setPreviewIsImage(false); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
            </div>
            {previewIsImage ? (
              <div style={{ width: '100%', height: 'calc(100% - 50px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                <img
                  src={previewUrl}
                  alt="Vista previa"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <iframe
                src={previewUrl}
                title="Vista previa PDF"
                style={{ width: '100%', height: 'calc(100% - 50px)', border: 'none' }}
              />
            )}
          </div>
        </div>
      )}

      {/* Expediente Digital */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12H15M9 16H15M17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H12.586C12.8512 3.00006 13.1055 3.10545 13.293 3.293L18.707 8.707C18.8946 8.89449 18.9999 9.1488 19 9.414V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21Z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3 className={styles.sectionTitle}>Expediente Digital</h3>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '16px', color: '#666' }}>Cargando documentos...</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: '#333' }}>Documento</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: '#333', textAlign: 'center' }}>Recibido</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: '#333' }}>Fecha de actualización</th>
                <th style={{ padding: '10px 12px', fontWeight: 600, color: '#333', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {TIPOS_DOCUMENTO.map((tipo) => {
                const doc = docPorTipo[tipo.key];
                const recibido = !!doc;
                const isUploading = uploading === tipo.key;

                return (
                  <tr key={tipo.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 12px' }}>
                      {tipo.label}
                      {doc && doc.transferido_de_interesado && (
                        <span style={{
                          display: 'inline-block', marginLeft: '8px', padding: '2px 8px',
                          background: '#e3f2fd', color: '#1565c0', borderRadius: '10px',
                          fontSize: '10px', fontWeight: 600, verticalAlign: 'middle',
                        }}>Transferido</span>
                      )}
                      {doc && doc.tamano_bytes ? (
                        <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>({formatSize(doc.tamano_bytes)})</span>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {recibido ? (
                        <span style={{ color: '#28a745', fontSize: '18px', fontWeight: 'bold' }}>✓</span>
                      ) : (
                        <span style={{ color: '#dc3545', fontSize: '18px', fontWeight: 'bold' }}>✗</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#666' }}>
                      {recibido ? formatDate(doc.fecha_actualizacion || doc.fecha_creacion) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <input
                        type="file"
                        accept={tipo.isImage ? 'image/jpeg,image/png,.jpg,.jpeg,.png' : 'application/pdf,.pdf'}
                        style={{ display: 'none' }}
                        ref={el => (fileInputRefs.current[tipo.key] = el)}
                        onChange={(e) => handleFileChange(tipo.key, e)}
                      />
                      {recibido ? (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleViewDocument(doc)}
                            title="Ver documento"
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                          >👁️</button>
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            title="Descargar"
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                          >⬇️</button>
                          <button
                            onClick={() => handleUploadClick(tipo.key)}
                            title="Reemplazar"
                            disabled={isUploading}
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                          >🔄</button>
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            title="Eliminar"
                            style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#dc3545' }}
                          >🗑️</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleUploadClick(tipo.key)}
                          disabled={isUploading}
                          style={{
                            background: '#ed8b00', color: '#fff', border: 'none',
                            borderRadius: '6px', padding: '6px 16px', cursor: 'pointer',
                            fontWeight: 500, fontSize: '13px',
                          }}
                        >
                          {isUploading ? 'Subiendo...' : (tipo.isImage ? '📷 Subir Foto' : '📤 Subir PDF')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Estadísticas */}
      <div className={styles.documentStats}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{docsRecibidos}</div>
          <div className={styles.statLabel}>Documentos Completos</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{totalDocs - docsRecibidos}</div>
          <div className={styles.statLabel}>Documentos Pendientes</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {totalDocs > 0 ? Math.round((docsRecibidos / totalDocs) * 100) : 0}%
          </div>
          <div className={styles.statLabel}>Completitud</div>
        </div>
      </div>
    </div>
  );
}

export default Documentos;
